"use client";

import { c as _c } from "react/compiler-runtime";
import { TableBody, TableCell, TableFoot, TableRow } from "../index";
import { SortableTableHead } from "../sortable-table-head";
import type { SortableHeader } from "../sortable-table-head";
import { useMemo } from "react";
import { cx } from "@/lib/tailwind-utils";
import { useAnalyticsGroupedTable } from "./context";
import { AnalyticsDataRow, AnalyticsGroupRow, AnalyticsSummaryRow } from "./table-rows";
import type { AnalyticsRow, AnalyticsSummary } from "./types";

// ============================================================================
// AnalyticsTableHeader
// ============================================================================

interface AnalyticsTableHeaderProps {
  groupedColumnLabel?: string;
}
export function AnalyticsTableHeader(t0) {
  const $ = _c(8);
  const {
    groupedColumnLabel
  } = t0;
  const {
    visibleColumns,
    isGrouped,
    sortFilters,
    setSortFilters
  } = useAnalyticsGroupedTable();
  let headers;
  if ($[0] !== groupedColumnLabel || $[1] !== isGrouped || $[2] !== visibleColumns) {
    headers = [];
    if (isGrouped) {
      headers.push({
        label: "",
        sortField: "none",
        className: "w-10"
      });
    }
    visibleColumns.forEach((col, index) => {
      const label = isGrouped && index === 0 && groupedColumnLabel ? groupedColumnLabel : col.label;
      const sortField = isGrouped && index === 0 && col.sortable ? "name" : col.sortable ? col.sortField ?? col.key : "none";
      headers.push({
        label,
        sortField,
        className: col.headerClassName,
        tooltip: col.tooltip
      });
    });
    $[0] = groupedColumnLabel;
    $[1] = isGrouped;
    $[2] = visibleColumns;
    $[3] = headers;
  } else {
    headers = $[3];
  }
  const sortableHeaders = headers;
  let t1;
  if ($[4] !== setSortFilters || $[5] !== sortFilters || $[6] !== sortableHeaders) {
    t1 = <SortableTableHead headers={sortableHeaders} filters={sortFilters} setFilters={setSortFilters} />;
    $[4] = setSortFilters;
    $[5] = sortFilters;
    $[6] = sortableHeaders;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}

// ============================================================================
// AnalyticsFlatTableBody
// ============================================================================

export function AnalyticsFlatTableBody() {
  const $ = _c(29);
  const {
    sortedData,
    visibleColumns,
    showGrandTotals,
    grandTotalsPosition,
    emptyMessage,
    pagination,
    itemLabel
  } = useAnalyticsGroupedTable();
  if (!sortedData) {
    return null;
  }
  const group = sortedData.groups[0];
  let t0;
  if ($[0] !== group?.rows) {
    t0 = group?.rows ?? [];
    $[0] = group?.rows;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const allRows = t0;
  const totalCount = group?.count ?? allRows.length;
  const totalPages = group?.totalPages ?? 1;
  const hasPagination = pagination && totalPages > 1;
  let t1;
  if ($[2] !== group?.key || $[3] !== pagination) {
    t1 = pagination?.isGroupLoading(group?.key ?? "all") ?? false;
    $[2] = group?.key;
    $[3] = pagination;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const isLoading = t1;
  const showTopTotal = showGrandTotals && allRows.length > 0 && grandTotalsPosition === "top";
  const showBottomTotal = showGrandTotals && allRows.length > 0 && grandTotalsPosition !== "top";
  let t2;
  if ($[5] !== showTopTotal || $[6] !== sortedData.grandTotals) {
    t2 = showTopTotal && <AnalyticsSummaryRow summary={sortedData.grandTotals} label="Total" isGrandTotal={true} />;
    $[5] = showTopTotal;
    $[6] = sortedData.grandTotals;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== allRows || $[9] !== emptyMessage || $[10] !== visibleColumns) {
    t3 = allRows.length === 0 ? <TableRow><TableCell colSpan={visibleColumns.length} className="py-8 text-center text-foreground-muted">{emptyMessage}</TableCell></TableRow> : allRows.map(_temp);
    $[8] = allRows;
    $[9] = emptyMessage;
    $[10] = visibleColumns;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <TableBody>{t2}{t3}</TableBody>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== group?.key || $[16] !== hasPagination || $[17] !== isLoading || $[18] !== itemLabel || $[19] !== pagination || $[20] !== showBottomTotal || $[21] !== sortedData.grandTotals || $[22] !== totalCount || $[23] !== totalPages || $[24] !== visibleColumns) {
    t5 = (showBottomTotal || hasPagination) && <TableFoot>{showBottomTotal && <AnalyticsSummaryRow summary={sortedData.grandTotals} label="Total" isGrandTotal={true} />}{hasPagination && <TableRow><TableCell colSpan={visibleColumns.length} className="p-0"><FlatTablePagination currentPage={pagination.getGroupPage(group?.key ?? "all")} totalPages={totalPages} totalItems={totalCount} pageSize={pagination.pageSize} onPageChange={page => pagination.setGroupPage(group?.key ?? "all", page)} isLoading={isLoading} itemLabel={itemLabel} /></TableCell></TableRow>}</TableFoot>;
    $[15] = group?.key;
    $[16] = hasPagination;
    $[17] = isLoading;
    $[18] = itemLabel;
    $[19] = pagination;
    $[20] = showBottomTotal;
    $[21] = sortedData.grandTotals;
    $[22] = totalCount;
    $[23] = totalPages;
    $[24] = visibleColumns;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== t4 || $[27] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  return t6;
}

// ============================================================================
// FlatTablePagination
// ============================================================================
function _temp(row, idx) {
  return <AnalyticsDataRow key={idx} row={row} groupKey={undefined} index={idx} />;
}
interface FlatTablePaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  itemLabel?: string;
}
function FlatTablePagination(t0) {
  const $ = _c(27);
  const {
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    onPageChange,
    isLoading,
    itemLabel: t1
  } = t0;
  const itemLabel = t1 === undefined ? "items" : t1;
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  let t2;
  if ($[0] !== endItem || $[1] !== itemLabel || $[2] !== startItem || $[3] !== totalItems) {
    t2 = <span className="text-body text-foreground-secondary">Showing {startItem}-{endItem} of {totalItems} {itemLabel}</span>;
    $[0] = endItem;
    $[1] = itemLabel;
    $[2] = startItem;
    $[3] = totalItems;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== currentPage || $[6] !== onPageChange) {
    t3 = () => onPageChange(currentPage - 1);
    $[5] = currentPage;
    $[6] = onPageChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = currentPage <= 1 || isLoading;
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <button type="button" onClick={t3} disabled={t4} className="rounded border bg-background px-3 py-1.5 text-body-strong text-foreground transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-50">Previous</button>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== currentPage || $[12] !== totalPages) {
    t6 = <span className="text-body text-foreground-secondary">Page {currentPage} of {totalPages}</span>;
    $[11] = currentPage;
    $[12] = totalPages;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== currentPage || $[15] !== onPageChange) {
    t7 = () => onPageChange(currentPage + 1);
    $[14] = currentPage;
    $[15] = onPageChange;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = currentPage >= totalPages || isLoading;
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <button type="button" onClick={t7} disabled={t8} className="rounded border bg-background px-3 py-1.5 text-body-strong text-foreground transition-colors hover:bg-hover disabled:cursor-not-allowed disabled:opacity-50">Next</button>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t9) {
    t10 = <div className="flex items-center gap-2">{t5}{t6}{t9}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t2) {
    t11 = <div className="flex items-center justify-between border-t px-4 py-3">{t2}{t10}</div>;
    $[24] = t10;
    $[25] = t2;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}

// ============================================================================
// AnalyticsGroupedTableBody
// ============================================================================

export function AnalyticsGroupedTableBody() {
  const $ = _c(51);
  const {
    sortedData,
    visibleColumns,
    isGrouped,
    showGrandTotals,
    grandTotalsPosition,
    isTrailingGroup,
    emptyMessage,
    renderSummaryLabel
  } = useAnalyticsGroupedTable();
  if (!sortedData) {
    return null;
  }
  const hasGroups = sortedData.groups.length > 0;
  let t0;
  if ($[0] !== isTrailingGroup || $[1] !== sortedData.groups) {
    t0 = isTrailingGroup ? sortedData.groups.filter(group => !isTrailingGroup(group)) : sortedData.groups;
    $[0] = isTrailingGroup;
    $[1] = sortedData.groups;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const leadingGroups = t0;
  let T0;
  let renderGrandTotalRow;
  let showBottomTotal;
  let t1;
  let t2;
  let t3;
  let t4;
  let totalGoesInline;
  if ($[3] !== emptyMessage || $[4] !== grandTotalsPosition || $[5] !== hasGroups || $[6] !== isGrouped || $[7] !== isTrailingGroup || $[8] !== leadingGroups || $[9] !== renderSummaryLabel || $[10] !== showGrandTotals || $[11] !== sortedData.grandTotals || $[12] !== sortedData.groups || $[13] !== visibleColumns) {
    const trailingGroups = isTrailingGroup ? sortedData.groups.filter(group_0 => isTrailingGroup(group_0)) : [];
    let t5;
    if ($[22] !== isGrouped || $[23] !== renderSummaryLabel || $[24] !== sortedData.grandTotals || $[25] !== visibleColumns) {
      t5 = () => <TableRow className="border-t-2 bg-muted font-bold">{isGrouped && <TableCell />}{visibleColumns.map((col, index) => <TableCell key={col.key} className={cx(col.cellClassName, "font-bold")}>{index === 0 ? renderSummaryLabel ? renderSummaryLabel("Grand Total", true) : <span className="text-body-emphasis">Grand Total</span> : col.renderSummary(sortedData.grandTotals, true)}</TableCell>)}</TableRow>;
      $[22] = isGrouped;
      $[23] = renderSummaryLabel;
      $[24] = sortedData.grandTotals;
      $[25] = visibleColumns;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    renderGrandTotalRow = t5;
    const showTopTotal = showGrandTotals && hasGroups && grandTotalsPosition === "top";
    showBottomTotal = showGrandTotals && hasGroups && grandTotalsPosition !== "top";
    totalGoesInline = showBottomTotal && trailingGroups.length > 0 && leadingGroups.length > 0;
    T0 = TableBody;
    if ($[27] !== renderGrandTotalRow || $[28] !== showTopTotal) {
      t1 = showTopTotal && renderGrandTotalRow();
      $[27] = renderGrandTotalRow;
      $[28] = showTopTotal;
      $[29] = t1;
    } else {
      t1 = $[29];
    }
    if ($[30] !== emptyMessage || $[31] !== leadingGroups || $[32] !== sortedData.groups.length || $[33] !== visibleColumns.length) {
      t2 = sortedData.groups.length === 0 ? <TableRow><TableCell colSpan={visibleColumns.length + 1} className="py-8 text-center text-foreground-muted">{emptyMessage}</TableCell></TableRow> : leadingGroups.map(_temp2);
      $[30] = emptyMessage;
      $[31] = leadingGroups;
      $[32] = sortedData.groups.length;
      $[33] = visibleColumns.length;
      $[34] = t2;
    } else {
      t2 = $[34];
    }
    if ($[35] !== renderGrandTotalRow || $[36] !== totalGoesInline) {
      t3 = totalGoesInline && renderGrandTotalRow();
      $[35] = renderGrandTotalRow;
      $[36] = totalGoesInline;
      $[37] = t3;
    } else {
      t3 = $[37];
    }
    t4 = trailingGroups.map(_temp3);
    $[3] = emptyMessage;
    $[4] = grandTotalsPosition;
    $[5] = hasGroups;
    $[6] = isGrouped;
    $[7] = isTrailingGroup;
    $[8] = leadingGroups;
    $[9] = renderSummaryLabel;
    $[10] = showGrandTotals;
    $[11] = sortedData.grandTotals;
    $[12] = sortedData.groups;
    $[13] = visibleColumns;
    $[14] = T0;
    $[15] = renderGrandTotalRow;
    $[16] = showBottomTotal;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = totalGoesInline;
  } else {
    T0 = $[14];
    renderGrandTotalRow = $[15];
    showBottomTotal = $[16];
    t1 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
    totalGoesInline = $[21];
  }
  let t5;
  if ($[38] !== T0 || $[39] !== t1 || $[40] !== t2 || $[41] !== t3 || $[42] !== t4) {
    t5 = <T0>{t1}{t2}{t3}{t4}</T0>;
    $[38] = T0;
    $[39] = t1;
    $[40] = t2;
    $[41] = t3;
    $[42] = t4;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  let t6;
  if ($[44] !== renderGrandTotalRow || $[45] !== showBottomTotal || $[46] !== totalGoesInline) {
    t6 = showBottomTotal && !totalGoesInline && <TableFoot>{renderGrandTotalRow()}</TableFoot>;
    $[44] = renderGrandTotalRow;
    $[45] = showBottomTotal;
    $[46] = totalGoesInline;
    $[47] = t6;
  } else {
    t6 = $[47];
  }
  let t7;
  if ($[48] !== t5 || $[49] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[48] = t5;
    $[49] = t6;
    $[50] = t7;
  } else {
    t7 = $[50];
  }
  return t7;
}
function _temp3(group_2) {
  return <AnalyticsGroupRow key={group_2.key} group={group_2} />;
}
function _temp2(group_1) {
  return <AnalyticsGroupRow key={group_1.key} group={group_1} />;
}